import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';
import StorageImage from '../components/StorageImage';
// Change request review: shows added/removed media with visual diff

interface DiffData {
  id: string;
  garageId: string;
  garageName: string;
  status: string;
  currentData: Record<string, unknown>;
  proposedChanges: Record<string, unknown>;
  rejectionReason: string | null;
  createdAt: string;
  reviewedAt: string | null;
}

function DiffField({ label, current, proposed, rtl }: { label: string; current: unknown; proposed: unknown; rtl?: boolean }) {
  const isChanged = JSON.stringify(current) !== JSON.stringify(proposed);
  // Spec 081a: Arabic values (keys ending in `Ar`) render right-to-left; long text keeps its line breaks.
  const textProps = rtl ? { dir: 'rtl' as const, lang: 'ar' } : {};
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '180px 1fr 1fr',
      gap: '1rem',
      padding: '0.5rem 0',
      borderBottom: '1px solid #f3f4f6',
      background: isChanged ? 'rgba(34, 197, 94, 0.05)' : 'transparent',
    }}>
      <span style={{ fontWeight: 600, color: '#1a1a2e', fontSize: '0.875rem' }}>{label}</span>
      <span {...textProps} style={{ color: '#6b7280', fontSize: '0.875rem', whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>{String(current ?? '-')}</span>
      <span {...textProps} style={{
        fontSize: '0.875rem',
        color: isChanged ? '#059669' : '#6b7280',
        fontWeight: isChanged ? 600 : 400,
        whiteSpace: 'pre-line',
        overflowWrap: 'anywhere',
      }}>{String(proposed ?? '-')}</span>
    </div>
  );
}

// Fields to hide from the generic diff view (handled separately or removed)
const HIDDEN_FIELDS = new Set(['numberOfWorkers', 'licenseNumber', 'licenseStorageKey', 'latitude', 'longitude']);

// Friendly labels for keys whose camelCase autoformat is awkward (e.g. "Iban" -> "IBAN")
const FIELD_LABELS: Record<string, string> = {
  accountHolder: 'Account Holder',
  bankName: 'Bank Name',
  iban: 'IBAN',
  // Spec 081a — garage "About" text
  description: 'Description (English)',
  descriptionAr: 'Description (Arabic)',
};

function DiffSection({ title, current, proposed }: { title: string; current: Record<string, unknown>; proposed: Record<string, unknown> }) {
  const allKeys = new Set([...Object.keys(current || {}), ...Object.keys(proposed || {})]);
  // Filter out hidden fields
  const visibleKeys = Array.from(allKeys).filter(k => !HIDDEN_FIELDS.has(k));

  return (
    <div style={{ marginBottom: '2rem' }}>
      <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#030c54', marginBottom: '0.75rem' }}>{title}</h3>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '180px 1fr 1fr',
        gap: '1rem',
        padding: '0.5rem 0',
        borderBottom: '2px solid #e5e7eb',
        marginBottom: '0.5rem',
      }}>
        <span style={{ fontWeight: 600, color: '#6b7280', fontSize: '0.75rem', textTransform: 'uppercase' }}>Field</span>
        <span style={{ fontWeight: 600, color: '#6b7280', fontSize: '0.75rem', textTransform: 'uppercase' }}>Current</span>
        <span style={{ fontWeight: 600, color: '#6b7280', fontSize: '0.75rem', textTransform: 'uppercase' }}>Proposed</span>
      </div>
      {visibleKeys.map((key) => (
        <DiffField
          key={key}
          label={FIELD_LABELS[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase())}
          current={(current || {})[key]}
          proposed={(proposed || {})[key]}
          rtl={key.endsWith('Ar')}
        />
      ))}

      {/* Map view for location changes */}
      {(('latitude' in (proposed || {})) || ('longitude' in (proposed || {}))) && (() => {
        const currentLat = (current || {}).latitude;
        const currentLng = (current || {}).longitude;
        const proposedLat = (proposed || {}).latitude;
        const proposedLng = (proposed || {}).longitude;
        // Never hardcode the key: a literal here ships in the public bundle and, worse, bypasses
        // the env var entirely, so secret rotation silently never reaches it.
        const mapKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
        return (
          <div style={{ marginTop: '1rem' }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '0.5rem' }}>Location on Map</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              {currentLat && currentLng ? (
                <div>
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Current</p>
                  <iframe title="Current Location" width="100%" height="180" style={{ border: '1px solid #e5e7eb', borderRadius: '6px' }} loading="lazy"
                    src={`https://www.google.com/maps/embed/v1/place?key=${mapKey}&q=${currentLat},${currentLng}&zoom=14`} />
                </div>
              ) : <div><p style={{ fontSize: '0.75rem', color: '#a3a3a3' }}>No current location</p></div>}
              {proposedLat && proposedLng ? (
                <div>
                  <p style={{ fontSize: '0.75rem', color: '#059669', fontWeight: 600, marginBottom: '0.25rem' }}>Proposed</p>
                  <iframe title="Proposed Location" width="100%" height="180" style={{ border: '2px solid #059669', borderRadius: '6px' }} loading="lazy"
                    src={`https://www.google.com/maps/embed/v1/place?key=${mapKey}&q=${proposedLat},${proposedLng}&zoom=14`} />
                </div>
              ) : <div><p style={{ fontSize: '0.75rem', color: '#a3a3a3' }}>No proposed location</p></div>}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default function GarageChangeRequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const auth = useAuth();
  const [diff, setDiff] = useState<DiffData | null>(null);
  const [loading, setLoading] = useState(true);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectError, setRejectError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const rejectTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (showRejectModal) {
      // Auto-focus the textarea so admins can start typing immediately
      const t = setTimeout(() => rejectTextareaRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [showRejectModal]);

  useEffect(() => {
    if (!showRejectModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !processing) {
        setShowRejectModal(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showRejectModal, processing]);

  useEffect(() => {
    if (auth.token && id) loadDiff();
  }, [id, auth.token]);

  const loadDiff = async () => {
    setLoading(true);
    try {
      const data = await api.get<DiffData>(`/api/garages/change-requests/${id}`, auth.token!);
      if (data && data.id) {
        setDiff(data);
      } else {
        console.error('Empty response for change request:', id);
      }
    } catch (err) {
      console.error('Failed to load change request:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!confirm('Are you sure you want to approve this change request?')) return;
    setProcessing(true);
    try {
      await api.patch(`/api/garages/change-requests/${id}/approve`, {}, auth.token!);
      navigate('/change-requests', { replace: true });
    } catch (err) {
      console.error('Failed to approve:', err);
      const msg = err instanceof Error ? err.message : 'Unknown error';
      alert('Failed to approve: ' + msg);
    } finally {
      setProcessing(false);
    }
  };

  const openRejectModal = () => {
    setRejectionReason('');
    setRejectError(null);
    setShowRejectModal(true);
  };

  const closeRejectModal = () => {
    if (processing) return;
    setShowRejectModal(false);
    setRejectError(null);
  };

  const handleReject = async () => {
    if (rejectionReason.trim().length < 10) {
      setRejectError('Please enter at least 10 characters explaining the rejection.');
      rejectTextareaRef.current?.focus();
      return;
    }
    setProcessing(true);
    setRejectError(null);
    try {
      await api.patch(`/api/garages/change-requests/${id}/reject`, { reason: rejectionReason.trim() }, auth.token!);
      navigate('/change-requests');
    } catch (err) {
      console.error('Failed to reject:', err);
      setRejectError(err instanceof Error ? err.message : 'Failed to reject. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) return <p>Loading...</p>;
  if (!diff) return <p>Change request not found.</p>;

  const currentGarageInfo = (diff.currentData?.garageInfo || {}) as Record<string, unknown>;
  const proposedGarageInfo = (diff.proposedChanges?.garageInfo || {}) as Record<string, unknown>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a1a2e', margin: 0 }}>Change Request Review</h1>
          <p style={{ color: '#6b7280', margin: '0.25rem 0 0' }}>{diff.garageName} — submitted {new Date(diff.createdAt).toLocaleDateString()}</p>
        </div>
        <span style={{
          padding: '0.25rem 0.75rem',
          borderRadius: '4px',
          fontSize: '0.8125rem',
          fontWeight: 600,
          background: diff.status === 'PENDING' ? '#fef3c7' : diff.status === 'APPROVED' ? '#d1fae5' : '#fee2e2',
          color: diff.status === 'PENDING' ? '#92400e' : diff.status === 'APPROVED' ? '#065f46' : '#991b1b',
        }}>{diff.status}</span>
      </div>

      {Object.keys(proposedGarageInfo).length > 0 && (
        <DiffSection title="Garage Info" current={currentGarageInfo} proposed={proposedGarageInfo} />
      )}

      {/* Working hours intentionally omitted — they are direct-edited via Settings, never approval-gated. */}

      {'media' in (diff.proposedChanges || {}) && (
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#030c54', marginBottom: '0.75rem' }}>Media</h3>
          {(() => {
            const media = diff.proposedChanges.media as Record<string, unknown> | undefined;
            if (!media) return <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Media changes included in this request.</p>;
            const galleryAdded = (media.galleryAdded || []) as string[];
            const galleryRemoved = (media.galleryRemoved || []) as string[];
            const currentMedia = (diff.currentData?.media || {}) as Record<string, unknown>;
            const currentGalleryKeys = (currentMedia.currentGalleryKeys || []) as string[];
            return (
              <div>
                {'logoStorageKey' in media && (
                  <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: '1rem', padding: '0.5rem 0', borderBottom: '1px solid #f3f4f6', background: 'rgba(34, 197, 94, 0.05)' }}>
                    <span style={{ fontWeight: 600, color: '#1a1a2e', fontSize: '0.875rem' }}>New Logo</span>
                    <StorageImage
                      storageKey={String(media.logoStorageKey)}
                      garageId={diff.garageId}
                      token={auth.token!}
                      alt="New logo"
                      style={{ width: 80, height: 80 }}
                    />
                  </div>
                )}

                {/* Current gallery */}
                {currentGalleryKeys.length > 0 && (
                  <div style={{ padding: '0.75rem 0', borderBottom: '1px solid #f3f4f6' }}>
                    <span style={{ fontWeight: 600, color: '#6b7280', fontSize: '0.8125rem', display: 'block', marginBottom: '0.5rem' }}>Current Gallery ({currentGalleryKeys.length})</span>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {currentGalleryKeys.map((key, i) => {
                        const isBeingRemoved = galleryRemoved.includes(key);
                        return (
                          <div key={i} style={{ position: 'relative', opacity: isBeingRemoved ? 0.4 : 1, border: isBeingRemoved ? '2px dashed #ef4444' : '2px solid transparent', borderRadius: '8px' }}>
                            <StorageImage storageKey={key} garageId={diff.garageId} token={auth.token!} alt={`Current ${i + 1}`} style={{ width: 80, height: 80 }} />
                            {isBeingRemoved && <span style={{ position: 'absolute', top: 2, right: 4, color: '#ef4444', fontWeight: 700, fontSize: '0.75rem' }}>REMOVE</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Added images */}
                {galleryAdded.length > 0 && (
                  <div style={{ padding: '0.75rem 0', borderBottom: '1px solid #f3f4f6', background: 'rgba(34, 197, 94, 0.05)' }}>
                    <span style={{ fontWeight: 600, color: '#059669', fontSize: '0.8125rem', display: 'block', marginBottom: '0.5rem' }}>+ {galleryAdded.length} Image(s) Added</span>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {galleryAdded.map((key, i) => (
                        <StorageImage key={i} storageKey={key} garageId={diff.garageId} token={auth.token!} alt={`Added ${i + 1}`} style={{ width: 80, height: 80, border: '2px solid #059669', borderRadius: '8px' }} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Removed images */}
                {galleryRemoved.length > 0 && (
                  <div style={{ padding: '0.75rem 0', borderBottom: '1px solid #f3f4f6', background: 'rgba(239, 68, 68, 0.05)' }}>
                    <span style={{ fontWeight: 600, color: '#ef4444', fontSize: '0.8125rem', display: 'block', marginBottom: '0.5rem' }}>- {galleryRemoved.length} Image(s) Removed</span>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {galleryRemoved.map((key, i) => (
                        <StorageImage key={i} storageKey={key} garageId={diff.garageId} token={auth.token!} alt={`Removed ${i + 1}`} style={{ width: 80, height: 80, border: '2px solid #ef4444', borderRadius: '8px', opacity: 0.5 }} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {diff.status === 'PENDING' && (
        <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem', paddingTop: '1.5rem', borderTop: '2px solid #e5e7eb' }}>
          <button
            onClick={handleApprove}
            disabled={processing}
            style={{
              padding: '0.625rem 2rem',
              background: '#059669',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {processing ? 'Processing...' : 'Approve'}
          </button>
          <button
            onClick={openRejectModal}
            disabled={processing}
            style={{
              padding: '0.625rem 2rem',
              background: '#ef4444',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Reject
          </button>
        </div>
      )}

      {showRejectModal && (() => {
        const trimmed = rejectionReason.trim();
        const remaining = Math.max(0, 10 - trimmed.length);
        const meetsMin = trimmed.length >= 10;
        return (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="reject-modal-title"
            onClick={closeRejectModal}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(15, 23, 42, 0.55)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '1rem',
              zIndex: 1000,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: '#fff',
                borderRadius: '12px',
                width: '100%',
                maxWidth: '520px',
                padding: '1.5rem',
                boxShadow: '0 20px 50px rgba(15, 23, 42, 0.25)',
              }}
            >
              <h2
                id="reject-modal-title"
                style={{ margin: 0, fontSize: '1.125rem', fontWeight: 700, color: '#1a1a2e' }}
              >
                Reject change request
              </h2>
              <p style={{ margin: '0.375rem 0 1rem', color: '#6b7280', fontSize: '0.875rem' }}>
                The partner will see this reason on their profile. Please be specific.
              </p>
              <label
                htmlFor="reject-reason"
                style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: '#374151', marginBottom: '0.375rem' }}
              >
                Reason for rejection
              </label>
              <textarea
                id="reject-reason"
                ref={rejectTextareaRef}
                value={rejectionReason}
                onChange={(e) => {
                  setRejectionReason(e.target.value);
                  if (rejectError) setRejectError(null);
                }}
                placeholder="e.g. The proposed phone number does not match the trade license."
                disabled={processing}
                style={{
                  width: '100%',
                  minHeight: '110px',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: `1px solid ${rejectError ? '#ef4444' : '#d1d5db'}`,
                  fontSize: '0.875rem',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                }}
              />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '0.5rem',
                  fontSize: '0.75rem',
                  color: meetsMin ? '#059669' : '#6b7280',
                }}
              >
                <span aria-live="polite">
                  {meetsMin ? 'Minimum length met' : `${remaining} more character${remaining === 1 ? '' : 's'} needed`}
                </span>
                <span style={{ color: '#9ca3af' }}>{trimmed.length} / 10 min</span>
              </div>

              {rejectError && (
                <div
                  role="alert"
                  style={{
                    marginTop: '0.75rem',
                    padding: '0.5rem 0.75rem',
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    color: '#991b1b',
                    borderRadius: '6px',
                    fontSize: '0.8125rem',
                  }}
                >
                  {rejectError}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.25rem' }}>
                <button
                  type="button"
                  onClick={closeRejectModal}
                  disabled={processing}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#fff',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontWeight: 500,
                    cursor: processing ? 'not-allowed' : 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleReject}
                  disabled={processing}
                  title={meetsMin ? '' : 'Enter at least 10 characters to enable'}
                  style={{
                    padding: '0.5rem 1.25rem',
                    background: meetsMin ? '#ef4444' : '#fca5a5',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: 600,
                    cursor: processing ? 'wait' : meetsMin ? 'pointer' : 'not-allowed',
                    opacity: processing ? 0.8 : 1,
                  }}
                >
                  {processing ? 'Rejecting…' : 'Reject request'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
